import { useCallback, useRef, useState } from "react";

export enum ReadStateKey {
  RELEASES = "releases",
  VERSIONS = "versions",
  ALERTS = "alerts",
}

/**
 * Custom hook to manage read/unread state for items using localStorage
 * @param key - The feature key to store read items (e.g., ReadStateKey.RELEASES, ReadStateKey.VERSIONS)
 * @returns Object containing functions to manage read state
 */
const useReadState = (key: ReadStateKey) => {
  const storageKey = `readItems_${key}`;

  // Initialize state from localStorage once on mount
  const [readItemsSet, setReadItemsSet] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const items: string[] = JSON.parse(stored);
        return new Set(items);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Error reading from localStorage for key "${storageKey}":`,
        error,
      );
      localStorage.removeItem(storageKey);
    }
    return new Set();
  });

  // Use ref to access latest state without causing dependency changes
  const readItemsSetRef = useRef(readItemsSet);
  readItemsSetRef.current = readItemsSet;

  /**
   * Get all read item IDs as an array
   * Stable function reference - won't cause re-renders
   */
  const getReadItems = useCallback((): string[] => {
    return Array.from(readItemsSetRef.current);
  }, []);

  /**
   * Check if an item is marked as read
   * Stable function reference - uses ref to avoid dependency issues
   */
  const isRead = useCallback((id: string): boolean => {
    return readItemsSetRef.current.has(id);
  }, []);

  /**
   * Mark an item as read
   * Stable function reference - only depends on storageKey (constant)
   */
  const markAsRead = useCallback(
    (id: string): void => {
      setReadItemsSet(prevSet => {
        // If already read, return same set to avoid unnecessary updates
        if (prevSet.has(id)) {
          return prevSet;
        }

        // Create new set with the added item
        const updatedSet = new Set(prevSet);
        updatedSet.add(id);

        // Sync to localStorage
        try {
          const itemsArray = Array.from(updatedSet);
          localStorage.setItem(storageKey, JSON.stringify(itemsArray));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            `Error writing to localStorage for key "${storageKey}":`,
            error,
          );
        }

        return updatedSet;
      });
    },
    [storageKey],
  );

  // Return stable object - functions never change reference
  // Include readItemsSet to trigger re-renders when state changes
  return { isRead, markAsRead, getReadItems, readItemsSet };
};

export default useReadState;
